/**
 * Web Vitals Performance Monitoring
 * Tracks Core Web Vitals (LCP, FID, CLS, FCP, TTFB, INP) and sends to analytics
 *
 * Usage: Automatically initializes on page load
 * Data is logged to console in development, can be sent to analytics endpoint
 */

(function() {
  'use strict';

  // Check if Web Vitals monitoring is enabled
  const WEB_VITALS_ENABLED = true;
  const DEBUG_MODE = window.location.hostname === 'localhost' || window.location.hostname.includes('myshopify.com');

  if (!WEB_VITALS_ENABLED) return;

  // Core Web Vitals thresholds (Google recommended)
  const THRESHOLDS = {
    LCP: { good: 2500, needsImprovement: 4000 }, // Largest Contentful Paint
    FID: { good: 100, needsImprovement: 300 },   // First Input Delay
    CLS: { good: 0.1, needsImprovement: 0.25 },  // Cumulative Layout Shift
    FCP: { good: 1800, needsImprovement: 3000 }, // First Contentful Paint
    TTFB: { good: 800, needsImprovement: 1800 }, // Time to First Byte
    INP: { good: 200, needsImprovement: 500 }    // Interaction to Next Paint
  };

  // Store metrics
  const metrics = {};

  /**
   * Get rating based on threshold
   */
  function getRating(metric, value) {
    const threshold = THRESHOLDS[metric];
    if (!threshold) return 'unknown';
    if (value <= threshold.good) return 'good';
    if (value <= threshold.needsImprovement) return 'needs-improvement';
    return 'poor';
  }

  /**
   * Format metric value for display
   */
  function formatValue(name, value) {
    if (name === 'CLS') return value.toFixed(3);
    return Math.round(value);
  }

  /**
   * Send metric to analytics
   */
  function sendToAnalytics(metric) {
    const { name, value, rating, navigationType } = metric;

    // Log to console in debug mode
    if (DEBUG_MODE) {
      const emoji = rating === 'good' ? '✅' : rating === 'needs-improvement' ? '⚠️' : '❌';
      console.log(`${emoji} ${name}: ${formatValue(name, value)}${name === 'CLS' ? '' : 'ms'} (${rating})`, {
        metric,
        threshold: THRESHOLDS[name]
      });
    }

    // Send to Google Analytics 4 if available
    if (typeof gtag !== 'undefined') {
      gtag('event', name, {
        value: Math.round(name === 'CLS' ? value * 1000 : value),
        metric_rating: rating,
        metric_value: value,
        metric_delta: metric.delta,
        navigation_type: navigationType || 'navigate'
      });
    }

    // Send to Klaviyo if available (for advanced segmentation)
    if (window._learnq) {
      window._learnq.push(['track', 'Web Vitals Metric', {
        metric_name: name,
        metric_value: value,
        metric_rating: rating,
        page_url: window.location.pathname,
        navigation_type: navigationType || 'navigate'
      }]);
    }

    // Custom analytics endpoint (replace with your own)
    if (window.fetch && !DEBUG_MODE) {
      fetch('/apps/analytics/web-vitals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...metric,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: Date.now()
        }),
        keepalive: true
      }).catch(() => {
        // Silently fail - don't impact user experience
      });
    }

    // Store metric
    metrics[name] = metric;

    // Dispatch custom event for theme developers
    window.dispatchEvent(new CustomEvent('webVitalsMetric', {
      detail: metric
    }));
  }

  /**
   * Lightweight Web Vitals implementation
   * Based on web-vitals library: https://github.com/GoogleChrome/web-vitals
   */

  // LCP - Largest Contentful Paint
  function observeLCP() {
    let lcpValue = 0;
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      lcpValue = lastEntry.renderTime || lastEntry.loadTime;
    });

    try {
      observer.observe({ type: 'largest-contentful-paint', buffered: true });

      // Report when page is hidden or unloaded
      const reportLCP = () => {
        if (lcpValue > 0) {
          sendToAnalytics({
            name: 'LCP',
            value: lcpValue,
            rating: getRating('LCP', lcpValue),
            navigationType: performance.navigation?.type || 'navigate'
          });
        }
        observer.disconnect();
      };

      ['visibilitychange', 'pagehide'].forEach(event => {
        addEventListener(event, reportLCP, { once: true, capture: true });
      });
    } catch (e) {
      // Browser doesn't support PerformanceObserver
    }
  }

  // FID - First Input Delay
  function observeFID() {
    let fidValue = 0;
    const observer = new PerformanceObserver((list) => {
      const firstInput = list.getEntries()[0];
      fidValue = firstInput.processingStart - firstInput.startTime;

      sendToAnalytics({
        name: 'FID',
        value: fidValue,
        rating: getRating('FID', fidValue),
        navigationType: performance.navigation?.type || 'navigate'
      });

      observer.disconnect();
    });

    try {
      observer.observe({ type: 'first-input', buffered: true });
    } catch (e) {
      // Browser doesn't support PerformanceObserver
    }
  }

  // CLS - Cumulative Layout Shift
  function observeCLS() {
    let clsValue = 0;
    let sessionValue = 0;
    let sessionEntries = [];

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!entry.hadRecentInput) {
          const firstSessionEntry = sessionEntries[0];
          const lastSessionEntry = sessionEntries[sessionEntries.length - 1];

          if (sessionValue &&
              entry.startTime - lastSessionEntry.startTime < 1000 &&
              entry.startTime - firstSessionEntry.startTime < 5000) {
            sessionValue += entry.value;
            sessionEntries.push(entry);
          } else {
            sessionValue = entry.value;
            sessionEntries = [entry];
          }

          if (sessionValue > clsValue) {
            clsValue = sessionValue;
          }
        }
      }
    });

    try {
      observer.observe({ type: 'layout-shift', buffered: true });

      // Report on visibility change
      const reportCLS = () => {
        sendToAnalytics({
          name: 'CLS',
          value: clsValue,
          rating: getRating('CLS', clsValue),
          navigationType: performance.navigation?.type || 'navigate'
        });
      };

      ['visibilitychange', 'pagehide'].forEach(event => {
        addEventListener(event, reportCLS, { once: true, capture: true });
      });
    } catch (e) {
      // Browser doesn't support PerformanceObserver
    }
  }

  // FCP - First Contentful Paint
  function observeFCP() {
    try {
      const paintEntries = performance.getEntriesByType('paint');
      const fcpEntry = paintEntries.find(entry => entry.name === 'first-contentful-paint');

      if (fcpEntry) {
        sendToAnalytics({
          name: 'FCP',
          value: fcpEntry.startTime,
          rating: getRating('FCP', fcpEntry.startTime),
          navigationType: performance.navigation?.type || 'navigate'
        });
      }
    } catch (e) {
      // Paint timing not supported
    }
  }

  // TTFB - Time to First Byte
  function observeTTFB() {
    try {
      const navigationTiming = performance.getEntriesByType('navigation')[0];
      if (navigationTiming) {
        const ttfb = navigationTiming.responseStart - navigationTiming.requestStart;

        sendToAnalytics({
          name: 'TTFB',
          value: ttfb,
          rating: getRating('TTFB', ttfb),
          navigationType: navigationTiming.type || 'navigate'
        });
      }
    } catch (e) {
      // Navigation timing not supported
    }
  }

  // INP - Interaction to Next Paint (newer metric, replacing FID)
  function observeINP() {
    if (!PerformanceObserver.supportedEntryTypes.includes('event')) return;

    let longestInteraction = 0;
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > longestInteraction) {
          longestInteraction = entry.duration;
        }
      }
    });

    try {
      observer.observe({ type: 'event', buffered: true, durationThreshold: 16 });

      // Report on visibility change
      const reportINP = () => {
        if (longestInteraction > 0) {
          sendToAnalytics({
            name: 'INP',
            value: longestInteraction,
            rating: getRating('INP', longestInteraction),
            navigationType: performance.navigation?.type || 'navigate'
          });
        }
        observer.disconnect();
      };

      ['visibilitychange', 'pagehide'].forEach(event => {
        addEventListener(event, reportINP, { once: true, capture: true });
      });
    } catch (e) {
      // Event timing not supported
    }
  }

  // Initialize all observers
  function init() {
    // Wait for page to be interactive
    if (document.readyState === 'complete') {
      start();
    } else {
      addEventListener('load', start, { once: true });
    }
  }

  function start() {
    observeLCP();
    observeFID();
    observeCLS();
    observeFCP();
    observeTTFB();
    observeINP();

    if (DEBUG_MODE) {
      console.log('🔍 Web Vitals monitoring initialized');

      // Summary after 10 seconds
      setTimeout(() => {
        console.log('📊 Web Vitals Summary:', metrics);
      }, 10000);
    }
  }

  // Expose API for developers
  window.webVitals = {
    getMetrics: () => metrics,
    isEnabled: () => WEB_VITALS_ENABLED
  };

  // Initialize
  init();

})();
